'use client';

import { ITimezoneOption } from 'my-poppies-b2b-types';
import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocalStorage } from 'usehooks-ts';

import useDeviceDetect from '@/hooks/useDeviceDetect';
import { useTimezoneSelect } from '@/hooks/useTimezoneSelect';
import { IDimmerState } from '@/types/IDimmerState';
import { IGlobalStateContextProps } from '@/types/IGlobalStateContextProps';
import { getLocalStorageKey } from '@/utils/getLocalStorageKey';
import { isProduction } from '@/utils/isProduction';
import { setLocalStorageValue } from '@/utils/setLocalStorageValue';

export const GlobalStateContext = createContext<IGlobalStateContextProps>({} as IGlobalStateContextProps);

export const GlobalStateContextProvider = ({ children }: PropsWithChildren) => {
  const portalRef = useRef(null);
  const { parseTimezone } = useTimezoneSelect({});
  const [timezone, setTimezone] = useState<ITimezoneOption>({} as ITimezoneOption); // TODO add init logic!!!! #89
  const [useDebugColor, setUseDebugColor] = useLocalStorage<boolean>(getLocalStorageKey('USE_DEBUG_COLOR'), false);
  const [mockApi, setMockApi] = useLocalStorage<boolean>(getLocalStorageKey('MOCK_API'), false);
  const [showDrawer, setShowDrawer] = useState(false);
  const [dimmerState, setDimmerState] = useState<IDimmerState>('HIDDEN');
  const { isMobile } = useDeviceDetect();

  // this helps us track the correct status of the dimmer,
  // and use it to detect it in other places, without it being
  // outdated:
  useEffect(() => {
    setLocalStorageValue('DIMMER_STATUS', dimmerState);
  }, [dimmerState]);

  const showDrawerEffective = useMemo(() => showDrawer && isMobile, [showDrawer, isMobile]);

  const dim = useCallback(() => {
    if (dimmerState === 'HIDDEN') {
      setDimmerState('SHOW_WITHOUT_SPINNER');
    }
  }, [dimmerState]);

  const undim = useCallback(() => {
    if (dimmerState !== 'HIDDEN') {
      setDimmerState('HIDDEN');
    }
  }, [dimmerState]);

  const setTimezoneSafe: Dispatch<SetStateAction<ITimezoneOption>> = useCallback(
    (newDesiredTimezone) => {
      const newDesiredTimezoneParsed = parseTimezone(newDesiredTimezone as ITimezoneOption);

      if (newDesiredTimezone && newDesiredTimezoneParsed.value !== timezone.value) {
        setTimezone(newDesiredTimezoneParsed);
        undim();
      }
    },
    [parseTimezone, timezone.value, undim]
  );

  const mockApiEffective = useMemo(() => (isProduction ? false : mockApi), [mockApi]);

  const value: IGlobalStateContextProps = useMemo((): IGlobalStateContextProps => {
    return {
      showDrawer: showDrawerEffective,
      setShowDrawer,
      dimmerState,
      setDimmerState,
      dim,
      undim,
      timezone,
      setTimezone: setTimezoneSafe,
      portalRef,
      mockApi: mockApiEffective,
      setMockApi,
      useDebugColor,
      setUseDebugColor,
    };
  }, [
    showDrawerEffective,
    dimmerState,
    dim,
    undim,
    timezone,
    setTimezoneSafe,
    mockApiEffective,
    setMockApi,
    useDebugColor,
    setUseDebugColor,
  ]);

  return <GlobalStateContext.Provider value={value}>{children}</GlobalStateContext.Provider>;
};
